import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import api from '../../api/axiosInstance';
import {
  Users,
  UserPlus,
  ChartBarIcon,
  PhoneIcon,
  CogIcon,
  PlusIcon,
  EyeIcon,
} from '../../lib/commonUtils';
import {
  RefreshButton,
  PageHeader,
  KPISkeleton as KPISkeletonCommon,
  ListSkeleton as ListSkeletonCommon,
} from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';

const fmtRel = (iso, locale = 'en-US') => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (diff < 60) return rtf.format(-Math.round(diff), 'second');
    if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
    if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
    return rtf.format(-Math.round(diff / 86400), 'day');
  } catch {
    return '—';
  }
};

const formatPlanName = (plan) => {
  if (!plan) return '—';
  return plan
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const StatCard = ({ title, value, icon: Icon, variant = 'primary', format = 'number' }) => {
  const color =
    variant === 'primary' ? 'text-primary-400' :
      variant === 'success' ? 'text-green-400' :
        variant === 'warning' ? 'text-amber-400' :
          variant === 'secondary' ? 'text-purple-400' : 'text-white/40';

  return (
    <div className="controlled-glass rounded-2xl p-6 inner-highlight group hover:border-white/20 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center min-w-0 flex-1">
          <div className="flex-shrink-0 mr-4">
            <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 group-hover:bg-white/10 transition-colors">
              <Icon className={`h-6 w-6 ${color}`} />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <dt className="text-[10px] font-bold text-white/40 uppercase tracking-widest">{title}</dt>
            <dd className="text-2xl font-bold text-white tracking-tight mt-1">
              {format === 'currency'
                ? `$${Number(value || 0).toLocaleString()}`
                : Number(value || 0).toLocaleString()}
            </dd>
          </div>
        </div>
      </div>
    </div>
  );
};

const KPISkeleton = () => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="card animate-pulse">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded bg-gray-200 mr-3 sm:mr-4" />
          <div className="flex-1">
            <div className="h-4 w-20 sm:w-24 bg-gray-200 rounded mb-2" />
            <div className="h-6 w-12 sm:w-16 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ListSkeleton = ({ rows = 5 }) => <ListSkeletonCommon rows={rows} />;

const RightPaneSkeleton = () => (
  <div className="controlled-glass rounded-2xl p-6 inner-highlight animate-pulse">
    <div className="h-5 w-40 sm:w-56 bg-white/5 rounded mb-4" />
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-4 w-28 sm:w-36 bg-white/5 rounded" />
            <div className="h-3 w-20 sm:w-24 bg-white/5 rounded" />
          </div>
          <div className="space-y-2 text-right">
            <div className="h-4 w-10 sm:w-12 bg-white/5 rounded ml-auto" />
            <div className="h-3 w-16 sm:w-20 bg-white/5 rounded ml-auto" />
          </div>
        </div>
      ))}
    </div>
    <div className="h-4 w-32 sm:w-40 bg-white/5 rounded mt-6 mb-4" />
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex items-center justify-between mt-3">
        <div className="h-4 w-32 sm:w-40 bg-white/5 rounded" />
        <div className="h-4 w-10 sm:w-12 bg-white/5 rounded" />
      </div>
    ))}
  </div>
);

const SuperAdminDashboard = () => {
  const { user } = useAuthStore();
  const { t, locale } = useLanguage();
  const { fetchAgents, fetchAnalytics, fetchSubscriptions, analytics, loading: analyticsLoadingFlag } = useAppStore();

  const [refreshing, setRefreshing] = useState(false);

  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [usersTotal, setUsersTotal] = useState(0);
  const [users, setUsers] = useState([]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await api.get('/auth/admin/users');
      const { total_users, users: list } = res.data || {};
      setUsersTotal(Number(total_users ?? list?.length ?? 0));
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      setUsersError(e?.response?.data?.detail || e?.message || t('failedToLoadUsers'));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setUsersError('');
    try {
      await Promise.allSettled([
        (async () => { try { await fetchAgents?.(); } catch { } })(),
        (async () => { try { await fetchAnalytics?.(); } catch { } })(),
        (async () => { try { await fetchSubscriptions?.(); } catch { } })(),
        (async () => { try { await loadUsers(); } catch { } })(),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchAgents, fetchAnalytics, fetchSubscriptions, loadUsers]);

  useEffect(() => {
    (async () => {
      fetchAgents?.();
      fetchAnalytics?.();
      fetchSubscriptions?.();
      await loadUsers();
    })();
  }, [fetchAgents, fetchAnalytics, fetchSubscriptions, loadUsers]);

  const activeSubscriptions = useMemo(
    () => users.filter(u => String(u.subscription_status).toLowerCase() === 'active').length,
    [users]
  );

  const newUsers7d = useMemo(() => {
    const now = Date.now();
    const week = 7 * 24 * 60 * 60 * 1000;
    return users.filter(u => {
      const d = new Date(u.created_at).getTime();
      return !Number.isNaN(d) && (now - d) <= week;
    }).length;
  }, [users]);

  const recentUsers = useMemo(() => {
    return [...users]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
      .map(u => ({
        id: u.id ?? u.user_id ?? u.uid,
        name: u.name || '—',
        company: u.company_name || '—',
        plan: formatPlanName(u.subscription_plan) || '—',
        joined: fmtRel(u.created_at, locale),
      }));
  }, [users]);

  const totalCalls = analytics?.overview?.total_calls || 0;

  const topPerformingAgents = useMemo(() => {
    const perf = analytics?.agent_performance || [];
    const byType = {};
    perf.forEach(p => {
      const key = p.agent_type || p.name || 'Agent';
      if (!byType[key]) byType[key] = { name: key, totalCalls: 0, successRateAccum: 0, n: 0 };
      byType[key].totalCalls += Number(p.calls || 0);
      byType[key].successRateAccum += Number(p.success_rate || 0);
      byType[key].n += 1;
    });
    const rows = Object.values(byType).map(r => ({
      name: r.name,
      totalCalls: r.totalCalls,
      successRate: r.n ? Math.round((r.successRateAccum / r.n) * 100) / 100 : 0,
    }));
    return rows.sort((a, b) => b.totalCalls - a.totalCalls).slice(0, 3);
  }, [analytics]);

  const topRatedAgents = useMemo(() => {
    const perf = Array.isArray(analytics?.agent_performance) ? analytics.agent_performance : [];
    const MIN_CALLS = 1;
    const rows = perf
      .map(p => ({
        name: p.name || p.agent_name || 'Agent',
        calls: Number(p.calls || 0),
        successRate: Number(p.success_rate || 0),
      }))
      .filter(p => p.calls >= MIN_CALLS)
      .sort((a, b) => b.successRate - a.successRate)
      .slice(0, 3)
      .map(p => ({
        ...p,
        successRate: Math.max(0, Math.min(100, Number.isFinite(p.successRate) ? p.successRate : 0)),
      }));
    return rows;
  }, [analytics]);

  const isLoading = usersLoading || analyticsLoadingFlag || refreshing;

  return (
    <div className="space-y-4 sm:space-y-8">
      <PageHeader
        title={t('platformOverview')}
        subtitle={`${t('welcomeBack')}, ${user?.name || 'Super Admin'}. ${t('happeningPlatform')}`}
      >
        <div className="flex items-center justify-between sm:justify-start space-x-3 w-full sm:w-auto">
          <RefreshButton onClick={handleRefresh} isLoading={isLoading} />
          <Link to="/settings" className="flex items-center px-4 py-2 border border-white/10 bg-white/5 hover:bg-white/10 text-xs font-bold uppercase tracking-widest rounded-xl transition-all group">
            <CogIcon className="h-4 w-4 mr-2 text-white/40 group-hover:text-white" />
            <span className="hidden sm:inline">{t('platformSettings')}</span>
            <span className="sm:hidden">{t('settings')}</span>
          </Link>
        </div>
      </PageHeader>

      {isLoading ? (
        <KPISkeleton />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard title={t('totalUsers')} value={usersTotal} icon={Users} variant="primary" />
          <StatCard title={t('activeSubscriptions')} value={activeSubscriptions} icon={ChartBarIcon} variant="success" />
          <StatCard title={t('newUsers7d')} value={newUsers7d} icon={UserPlus} variant="warning" />
          <StatCard title={t('totalCalls')} value={totalCalls} icon={PhoneIcon} variant="secondary" />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="controlled-glass rounded-2xl p-6 inner-highlight">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white tracking-tight">{t('recentUsers')}</h2>
            <Link to="/user" className="text-xs font-bold text-primary-400 hover:text-primary-300 uppercase tracking-widest">
              {t('viewAll')}
            </Link>
          </div>

          {(usersLoading || refreshing) && <ListSkeleton rows={5} />}
          {usersError && !(usersLoading || refreshing) && <p className="text-sm text-red-600">{usersError}</p>}

          {!(usersLoading || refreshing) && !usersError && (
            <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
              {recentUsers.map((u, i) => {
                const hasId = Boolean(u.id);
                const Row = (
                  <div className="flex items-center justify-between p-3 hover:bg-gray-50 transition">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{u.company}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.plan}</p>
                      <p className="text-xs sm:text-sm text-gray-500">{u.joined}</p>
                    </div>
                  </div>
                );
                return hasId ? (
                  <Link key={i} to={`/user/${u.id}`}>{Row}</Link>
                ) : (
                  <div key={i}>{Row}</div>
                );
              })}
              {recentUsers.length === 0 && <p className="text-sm text-gray-500 p-3">No users yet.</p>}
            </div>
          )}
        </div>

        {isLoading ? (
          <RightPaneSkeleton />
        ) : (
          <div className="controlled-glass rounded-2xl p-6 inner-highlight">
            <h2 className="text-xl font-bold text-white tracking-tight mb-6">{t('topPerformingAgentTypes')}</h2>
            <div className="space-y-4">
              {topPerformingAgents.map((agent, index) => (
                <div key={index} className="flex items-center justify-between group p-2 hover:bg-white/5 rounded-xl transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">{agent.name}</p>
                    <p className="text-xs text-white/40 mt-0.5">{agent.totalCalls} {t('totalCallsCounter')}</p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-sm font-bold text-green-400">
                      {Number.isFinite(agent.successRate) ? `${agent.successRate}%` : '0%'}
                    </p>
                    <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{t('successRate')}</p>
                  </div>
                </div>
              ))}
              {topPerformingAgents.length === 0 && (
                <p className="text-sm text-white/30 italic">{t('noAgentData')}</p>
              )}
            </div>

            <div className="border-t border-white/5 mt-6 pt-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">{t('topRatedAgents')}</h3>
              <div className="space-y-3">
                {topRatedAgents.map((a, i) => (
                  <div key={i} className="flex items-center justify-between group p-2 hover:bg-white/5 rounded-xl transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white/80 group-hover:text-white truncate">{a.name}</p>
                      <p className="text-xs text-white/30">{a.calls} {t('calls')}</p>
                    </div>
                    <div className="text-right ml-3 text-sm font-bold text-green-400">
                      <span>{a.successRate.toFixed(2)}%</span>
                    </div>
                  </div>
                ))}
                {topRatedAgents.length === 0 && (
                  <p className="text-sm text-white/30 italic">{t('noRatedAgents')}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="controlled-glass rounded-2xl p-6 inner-highlight">
        <h2 className="text-xl font-bold text-white tracking-tight mb-6">{t('quickActions')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link to="/agents/create" className="flex items-center p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all group">
            <PlusIcon className="h-5 w-5 text-primary-400 mr-3 transition-transform group-hover:scale-110" />
            <span className="text-sm font-bold text-white/70 group-hover:text-white">{t('createNewAgent')}</span>
          </Link>
          <Link to="/call-logs" className="flex items-center p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all group">
            <PhoneIcon className="h-5 w-5 text-blue-400 mr-3 transition-transform group-hover:scale-110" />
            <span className="text-sm font-bold text-white/70 group-hover:text-white">{t('callLogs')}</span>
          </Link>
          <Link to="/training" className="flex items-center p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all group">
            <ChartBarIcon className="h-5 w-5 text-green-400 mr-3 transition-transform group-hover:scale-110" />
            <span className="text-sm font-bold text-white/70 group-hover:text-white">{t('agentTraining')}</span>
          </Link>
          <Link to="/analytics" className="flex items-center p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all group">
            <EyeIcon className="h-5 w-5 text-purple-400 mr-3 transition-transform group-hover:scale-110" />
            <span className="text-sm font-bold text-white/70 group-hover:text-white">{t('viewAnalytics')}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;